import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Download, Edit, DollarSign, Eye, CheckCircle2, Clock, RefreshCw, FileText } from 'lucide-react';
import { ContratoWithDetails } from '@/types/contrato';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import ContratoResumoDialog from './ContratoResumoDialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { financeiroService, calcularRecebimentosPrevistos } from '@/services/financeiroService';
import { toast } from 'sonner';

interface ContratoListaProps {
  contratos: ContratoWithDetails[];
  deletandoContrato: string | null;
  onEditar: (contrato: ContratoWithDetails) => void;
  onDeletar: (contratoId: string) => void;
  onReverterFinalizacao?: (contratoId: string) => void;
  onAtualizarStatus?: (contratoId: string, status: string) => Promise<void>;
  onGerarPDF?: (contratoId: string) => Promise<void>;
}

export default function ContratoLista({ 
  contratos, 
  deletandoContrato, 
  onEditar, 
  onDeletar,
  onReverterFinalizacao,
  onAtualizarStatus,
  onGerarPDF
}: ContratoListaProps) {
  const { userRole } = useAuthorization();
  const isVendedor = userRole === 'vendedor';
  const isParceiro = userRole === 'parceiro';
  const isRestricted = isVendedor || isParceiro;
  const isAdminOrFinanceiro = userRole === 'admin' || userRole === 'financeiro';
  const [contratoResumo, setContratoResumo] = useState<ContratoWithDetails | null>(null);
  
  // Estados para atualização de status
  const [contratoParaStatus, setContratoParaStatus] = useState<ContratoWithDetails | null>(null);
  const [novoStatus, setNovoStatus] = useState<string>('');
  const [atualizandoStatus, setAtualizandoStatus] = useState(false);
  
  // Estado para controle de geração de PDF
  const [gerandoPDF, setGerandoPDF] = useState<string | null>(null);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'rascunho': { label: 'Rascunho', variant: 'secondary' as const },
      'proposta_aceita': { label: 'Proposta Aceita', variant: 'default' as const },
      'contrato_gerado': { label: 'Não assinado', variant: 'default' as const },
      'assinado': { label: 'Assinado', variant: 'default' as const },
      'em_andamento': { label: 'Em Andamento', variant: 'outline' as const, className: 'border-blue-500 text-blue-500' },
      'finalizado': { label: 'Finalizado', variant: 'default' as const, className: 'bg-success text-white' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.rascunho;
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const handleAtualizarStatus = async () => {
    if (!contratoParaStatus || !novoStatus) return;
    
    if (novoStatus === 'finalizado') {
      try {
        setAtualizandoStatus(true);
        // Verificar se todos os recebimentos foram pagos (regra do financeiro)
        const contratoId = contratoParaStatus.id;
        
        // Buscar contas a receber para este contrato
        const contas = await financeiroService.listarContasReceber();
        const contasDoContrato = contas.filter(c => c.contrato_id === contratoId);
        
        // Usar a mesma lógica do componente financeiro
        // Convertemos ContratoWithDetails para o formato que calcularRecebimentosPrevistos espera
        // (que é compatível pois os campos integracao_* existem em ambos)
        const recebimentosPrevistos = calcularRecebimentosPrevistos(contratoParaStatus as any);
        
        if (recebimentosPrevistos.length === 0) {
          toast.error('Este contrato não possui recebimentos previstos configurados. Não é possível finalizar.');
          return;
        }

        const todosPagos = recebimentosPrevistos.every(recebimento => {
          const descricaoBase = recebimento.descricao.split(' - Parcela')[0];
          const contasRecebidas = contasDoContrato.filter(conta => 
            conta.status === 'recebido' &&
            (conta.descricao.includes(descricaoBase) || conta.descricao.includes(recebimento.descricao))
          );
          
          const totalRecebido = contasRecebidas.reduce((acc, conta) => acc + (conta.valor || 0), 0);
          return totalRecebido >= recebimento.valor;
        });

        if (!todosPagos) {
          toast.error('Não é possível finalizar: existem parcelas pendentes de recebimento.');
          return;
        }
      } catch (error) {
        console.error('Erro ao validar recebimentos:', error);
        toast.error('Erro ao validar recebimentos do contrato');
        return;
      } finally {
        setAtualizandoStatus(false);
      }
    }

    try {
      setAtualizandoStatus(true);
      if (onAtualizarStatus) {
        await onAtualizarStatus(contratoParaStatus.id, novoStatus);
        setContratoParaStatus(null);
        setNovoStatus('');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    } finally {
      setAtualizandoStatus(false);
    }
  };

  // Separar contratos em finalizados e em aberto
  const { contratosFinalizados, contratosEmAberto } = useMemo(() => {
    const finalizados = contratos.filter(c => c.status === 'finalizado');
    const emAberto = contratos.filter(c => c.status !== 'finalizado');
    return { contratosFinalizados: finalizados, contratosEmAberto: emAberto };
  }, [contratos]);

  const renderListaContratos = (contratosParaRenderizar: ContratoWithDetails[]) => {
    if (contratosParaRenderizar.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>Nenhum contrato encontrado</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {contratosParaRenderizar.map((contrato) => (
            <Card key={contrato.id}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg truncate">
                      {contrato.lead?.nome || contrato.lead?.razao_social}
                      {contrato.lead?.vendedor_nome && contrato.lead.vendedor_nome !== 'Não informado' && (
                        <span className="text-xs sm:text-sm text-blue-600 font-normal ml-1 sm:ml-2">
                          - {contrato.lead.vendedor_nome}
                        </span>
                      )}
                    </h3>
                    {contrato.lead?.parceiro_nome && contrato.lead.parceiro_nome !== 'Não informado' && (
                      <p className="text-xs sm:text-sm text-green-600 font-medium mt-1">
                        🤝 Parceiro: {contrato.lead.parceiro_nome}
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {(contrato.lead?.tipo === 'PF' || contrato.lead?.tipo === 'fisica') ? 'Pessoa Física' : 'Pessoa Jurídica'}
                    </p>
                    {contrato.dimensionamento?.nome_dimensionamento && (
                      <p className="text-xs sm:text-sm text-blue-600 font-medium">
                        📋 {contrato.dimensionamento.nome_dimensionamento}
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Criado em: {new Date(contrato.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex flex-col sm:items-end gap-3 sm:gap-2">
                    <div className="text-left sm:text-right">
                      <p className="font-semibold text-base sm:text-lg">
                        R$ {contrato.valor_total?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <div className="mt-2 sm:mb-2">
                        {getStatusBadge(contrato.status)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {!isParceiro && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setContratoResumo(contrato)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 text-xs sm:text-sm"
                        >
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Ver Resumo</span>
                          <span className="sm:hidden">Ver</span>
                        </Button>
                      )}
                      {!isParceiro && (
                        contrato.contrato_pdf_url ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(contrato.contrato_pdf_url, '_blank')}
                            className="text-xs sm:text-sm"
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                            <span className="hidden sm:inline">PDF</span>
                          </Button>
                        ) : (
                          onGerarPDF && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  setGerandoPDF(contrato.id);
                                  await onGerarPDF(contrato.id);
                                } catch (error) {
                                  // Erro já tratado no handler
                                } finally {
                                  setGerandoPDF(null);
                                }
                              }}
                              disabled={gerandoPDF === contrato.id}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 text-xs sm:text-sm"
                            >
                              {gerandoPDF === contrato.id ? (
                                <>
                                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 animate-spin" />
                                  <span className="hidden sm:inline">Gerando...</span>
                                  <span className="sm:hidden">...</span>
                                </>
                              ) : (
                                <>
                                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                                  <span className="hidden sm:inline">Gerar PDF</span>
                                  <span className="sm:hidden">PDF</span>
                                </>
                              )}
                            </Button>
                          )
                        )
                      )}
                      {/* Botão de Reverter Finalização - Apenas para Admins/Financeiro em contratos finalizados */}
                      {isAdminOrFinanceiro && contrato.status === 'finalizado' && onReverterFinalizacao && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onReverterFinalizacao(contrato.id)}
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-200 text-xs sm:text-sm"
                        >
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Reverter</span>
                          <span className="sm:hidden">Rev</span>
                        </Button>
                      )}
                      {/* Botões de Atualizar Status e Deletar - Apenas para Admins e apenas para contratos não finalizados */}
                      {!isRestricted && contrato.status !== 'finalizado' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setContratoParaStatus(contrato);
                              setNovoStatus(contrato.status);
                            }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs sm:text-sm"
                          >
                            <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Atualizar Status</span>
                            <span className="sm:hidden">Status</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onDeletar(contrato.id)}
                            disabled={deletandoContrato === contrato.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs sm:text-sm"
                          >
                            {deletandoContrato === contrato.id ? (
                              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 animate-spin" />
                            ) : (
                              <span className="text-base">🗑️</span>
                            )}
                            <span className="ml-1 sm:ml-0 sm:inline">Deletar</span>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
    );
  };

  if (contratos.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Contratos Existentes</CardTitle>
          <CardDescription>
            Lista de todos os contratos criados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="em-aberto" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="em-aberto" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Em Aberto</span>
                <span className="sm:hidden">Aberto</span>
                ({contratosEmAberto.length})
              </TabsTrigger>
              <TabsTrigger value="finalizados" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Finalizados</span>
                <span className="sm:hidden">Final</span>
                ({contratosFinalizados.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="em-aberto" className="mt-4">
              {renderListaContratos(contratosEmAberto)}
            </TabsContent>
            <TabsContent value="finalizados" className="mt-4">
              {renderListaContratos(contratosFinalizados)}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Seção de Comissões - Para Vendedores e Parceiros (apenas contratos em aberto) */}
      {isRestricted && contratosEmAberto.length > 0 && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 mt-4 sm:mt-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              <CardTitle className="text-base sm:text-lg">Resumo de Comissões</CardTitle>
            </div>
            <CardDescription className="text-xs sm:text-sm">
              Valores de comissões por contrato firmado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {contratosEmAberto.map((contrato) => (
                <div 
                  key={contrato.id} 
                  className="bg-white rounded-lg p-3 sm:p-4 border border-blue-100 shadow-sm"
                >
                  <div className="space-y-2 sm:space-y-3">
                    {/* Nome do Cliente */}
                    <div className="flex items-start justify-between border-b pb-2 gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">Cliente</p>
                        <p className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                          {contrato.lead?.nome || contrato.lead?.razao_social || 'Não informado'}
                        </p>
                        {contrato.lead?.parceiro_nome && contrato.lead.parceiro_nome !== 'Não informado' && (
                          <p className="text-xs text-blue-600 mt-1 truncate">
                            Parceiro: {contrato.lead.parceiro_nome}
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-muted-foreground">Status</p>
                        <div className="mt-1">
                          {getStatusBadge(contrato.status)}
                        </div>
                      </div>
                    </div>

                    {/* Valor do Contrato */}
                    <div className="bg-gray-50 rounded p-2 sm:p-3">
                      <p className="text-xs text-muted-foreground mb-1">Valor Total do Contrato</p>
                      <p className="text-base sm:text-lg font-bold text-gray-900">
                        R$ {(Math.round((Number(contrato.valor_total) || 0) * 100) / 100).toLocaleString('pt-BR', { 
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2 
                        })}
                      </p>
                    </div>

                    {/* Comissões */}
                    <div className={`grid gap-3 ${isRestricted ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      {/* Comissão Vendedor - Ocultar para Parceiros */}
                      {!isParceiro && (
                        <div className={`${isVendedor ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} rounded p-3 border`}>
                          <p className={`text-xs ${isVendedor ? 'text-green-700' : 'text-blue-700'} mb-1 font-medium`}>
                            {isVendedor ? 'Sua Comissão' : 'Comissão Vendedor'}
                          </p>
                          <p className={`text-base font-bold ${isVendedor ? 'text-green-700' : 'text-blue-700'}`}>
                            R$ {(Math.round((Number(contrato.proposta?.comissao_vendedor_valor) || 0) * 100) / 100).toLocaleString('pt-BR', { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2 
                            })}
                          </p>
                        </div>
                      )}
                      
                      {/* Comissão Parceiro - Mostrar para todos quando houver valor */}
                      {contrato.proposta?.comissao_parceiro_valor !== undefined && contrato.proposta.comissao_parceiro_valor > 0 && (
                        <div className={`${isParceiro ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'} rounded p-3 border`}>
                          <p className={`text-xs ${isParceiro ? 'text-green-700' : 'text-blue-700'} mb-1 font-medium`}>
                            {isParceiro ? 'Sua Comissão' : 'Comissão Parceiro'}
                          </p>
                          <p className={`text-base font-bold ${isParceiro ? 'text-green-700' : 'text-blue-700'}`}>
                            R$ {(Math.round((Number(contrato.proposta.comissao_parceiro_valor) || 0) * 100) / 100).toLocaleString('pt-BR', { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2 
                            })}
                          </p>
                        </div>
                      )}

                      {/* Comissão Parceiro Pai */}
                      {contrato.proposta?.comissao_parceiro_pai_valor !== undefined && contrato.proposta.comissao_parceiro_pai_valor > 0 && (
                        <div className="bg-blue-50 border-blue-200 rounded p-3 border">
                          <p className="text-xs text-blue-700 mb-1 font-medium">
                            Comissão Parceiro Pai
                          </p>
                          <p className="text-base font-bold text-blue-700">
                            R$ {(Math.round((Number(contrato.proposta.comissao_parceiro_pai_valor) || 0) * 100) / 100).toLocaleString('pt-BR', { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2 
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Data de Geração */}
                    <div className="text-xs text-muted-foreground pt-2 border-t">
                      <p>Contrato gerado em: {new Date(contrato.created_at).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Resumo do Contrato */}
      {contratoResumo && (
        <ContratoResumoDialog
          open={!!contratoResumo}
          onOpenChange={(open) => !open && setContratoResumo(null)}
          contrato={contratoResumo}
        />
      )}

      {/* Dialog de Atualização de Status */}
      <Dialog open={!!contratoParaStatus} onOpenChange={(open) => !open && setContratoParaStatus(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar Status do Contrato</DialogTitle>
            <DialogDescription>
              Selecione o novo status para o contrato de {contratoParaStatus?.lead?.nome || contratoParaStatus?.lead?.razao_social}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="status">Novo Status</Label>
              <Select value={novoStatus} onValueChange={setNovoStatus}>
                <SelectTrigger id="status">
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contrato_gerado">Não assinado</SelectItem>
                  <SelectItem value="assinado">Assinado</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="finalizado">Finalizado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setContratoParaStatus(null)} disabled={atualizandoStatus}>
              Cancelar
            </Button>
            <Button onClick={handleAtualizarStatus} disabled={atualizandoStatus || !novoStatus}>
              {atualizandoStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Atualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
